const JALALI_LOCALE = "fa-AF-u-ca-persian-nu-latn";
export const JALALI_MONTH_NAMES = [
  "حمل",
  "ثور",
  "جوزا",
  "سرطان",
  "اسد",
  "سنبله",
  "میزان",
  "عقرب",
  "قوس",
  "جدی",
  "دلو",
  "حوت",
];
const MONTH_NAME_TO_NUMBER = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,
};

const toDate = (value = new Date()) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [year, month, day] = trimmed.split("-").map(Number);
      return new Date(year, month - 1, day);
    }
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatWithOptions = (value, options) => {
  const date = toDate(value);
  if (!date) return "--";

  return new Intl.DateTimeFormat(JALALI_LOCALE, options).format(date);
};

const normalizeMonthNumber = (monthNumber) => {
  const text = String(monthNumber || "").trim();
  if (!text) return null;
  const namedMonth = MONTH_NAME_TO_NUMBER[text.toLowerCase()];
  const month = namedMonth || Number(text);
  return Number.isInteger(month) && month >= 1 && month <= 12 ? month : null;
};

const pad2 = (value) => String(value).padStart(2, "0");

const div = (a, b) => ~~(a / b);

export const formatJalaliParts = (year, month, day) => `${year}/${pad2(month)}/${pad2(day)}`;

export const formatJalaliMonthParts = (year, month) => `${year}/${pad2(month)}`;

export const gregorianToJalaliParts = (gy, gm, gd) => {
  const gDaysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const jDaysInMonth = [31, 31, 31, 31, 31, 31, 30, 30, 30, 30, 30, 29];

  let gy2 = gy - 1600;
  let gm2 = gm - 1;
  let gd2 = gd - 1;

  let gDayNo =
    365 * gy2 +
    div(gy2 + 3, 4) -
    div(gy2 + 99, 100) +
    div(gy2 + 399, 400);

  for (let i = 0; i < gm2; ++i) gDayNo += gDaysInMonth[i];
  if (gm2 > 1 && ((gy % 4 === 0 && gy % 100 !== 0) || gy % 400 === 0)) {
    gDayNo += 1;
  }
  gDayNo += gd2;

  let jDayNo = gDayNo - 79;
  const jNp = div(jDayNo, 12053);
  jDayNo %= 12053;

  let jy = 979 + 33 * jNp + 4 * div(jDayNo, 1461);
  jDayNo %= 1461;

  if (jDayNo >= 366) {
    jy += div(jDayNo - 1, 365);
    jDayNo = (jDayNo - 1) % 365;
  }

  let jm = 0;
  for (; jm < 11 && jDayNo >= jDaysInMonth[jm]; ++jm) {
    jDayNo -= jDaysInMonth[jm];
  }

  return { year: jy, month: jm + 1, day: jDayNo + 1 };
};

export const jalaliToGregorianParts = (jy, jm, jd) => {
  const gDaysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const jDaysInMonth = [31, 31, 31, 31, 31, 31, 30, 30, 30, 30, 30, 29];

  let jy2 = jy - 979;
  let jm2 = jm - 1;
  let jd2 = jd - 1;

  let jDayNo = 365 * jy2 + div(jy2, 33) * 8 + div((jy2 % 33) + 3, 4);
  for (let i = 0; i < jm2; ++i) jDayNo += jDaysInMonth[i];
  jDayNo += jd2;

  let gDayNo = jDayNo + 79;
  let gy = 1600 + 400 * div(gDayNo, 146097);
  gDayNo %= 146097;

  let leap = true;
  if (gDayNo >= 36525) {
    gDayNo -= 1;
    gy += 100 * div(gDayNo, 36524);
    gDayNo %= 36524;

    if (gDayNo >= 365) {
      gDayNo += 1;
    } else {
      leap = false;
    }
  }

  gy += 4 * div(gDayNo, 1461);
  gDayNo %= 1461;

  if (gDayNo >= 366) {
    leap = false;
    gDayNo -= 1;
    gy += div(gDayNo, 365);
    gDayNo %= 365;
  }

  let i = 0;
  for (
    ;
    gDayNo >= gDaysInMonth[i] + (i === 1 && leap ? 1 : 0);
    i += 1
  ) {
    gDayNo -= gDaysInMonth[i] + (i === 1 && leap ? 1 : 0);
  }

  return { year: gy, month: i + 1, day: gDayNo + 1 };
};

export const getJalaliMonthLength = (year, month) => {
  const nextYear = month === 12 ? year + 1 : year;
  const nextMonth = month === 12 ? 1 : month + 1;
  const start = jalaliToGregorianParts(year, month, 1);
  const end = jalaliToGregorianParts(nextYear, nextMonth, 1);
  const startDate = Date.UTC(start.year, start.month - 1, start.day);
  const endDate = Date.UTC(end.year, end.month - 1, end.day);
  return Math.round((endDate - startDate) / 86400000);
};

const getGregorianParts = (value) => {
  const date = toDate(value);
  if (!date) return null;
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
  };
};

const parseJalaliParts = (value) => {
  const match = String(value || "")
    .trim()
    .match(/^(\d{4})[/-](\d{1,2})(?:[/-](\d{1,2}))?/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3] || 1);
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return null;
  }
  if (day > getJalaliMonthLength(year, month)) return null;
  return { year, month, day };
};

const parseTimeParts = (value) => {
  const match = String(value || "").match(/(?:T|\s)(\d{1,2}):(\d{2})/);
  if (!match) return { hour: 0, minute: 0 };
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return null;
  }
  return { hour, minute };
};

export const formatJalaliInputDate = (value) => {
  const parts = getGregorianParts(value);
  if (!parts) return "";
  const jalali = gregorianToJalaliParts(parts.year, parts.month, parts.day);
  return formatJalaliParts(jalali.year, jalali.month, jalali.day);
};

export const formatJalaliInputDateTime = (value) => {
  const parts = getGregorianParts(value);
  if (!parts) return "";
  return `${formatJalaliInputDate(value)} ${pad2(parts.hour)}:${pad2(parts.minute)}`;
};

export const formatJalaliInputMonth = (value) => {
  const text = String(value || "").trim();
  const match = text.match(/^(\d{4})-(\d{1,2})/);
  if (!match) return "";
  const jalali = gregorianToJalaliParts(Number(match[1]), Number(match[2]), 15);
  return formatJalaliMonthParts(jalali.year, jalali.month);
};

export const parseJalaliDateToGregorian = (value) => {
  if (!String(value || "").trim()) return "";
  const parts = parseJalaliParts(value);
  if (!parts) return null;
  const gregorian = jalaliToGregorianParts(parts.year, parts.month, parts.day);
  return `${gregorian.year}-${pad2(gregorian.month)}-${pad2(gregorian.day)}`;
};

export const parseJalaliDateTimeToGregorian = (value) => {
  if (!String(value || "").trim()) return "";
  const date = parseJalaliDateToGregorian(value);
  const time = parseTimeParts(value);
  if (date === null || time === null) return null;
  return `${date}T${pad2(time.hour)}:${pad2(time.minute)}`;
};

export const parseJalaliMonthToGregorian = (value) => {
  if (!String(value || "").trim()) return "";
  const parts = parseJalaliParts(value);
  if (!parts) return null;
  const gregorian = jalaliToGregorianParts(parts.year, parts.month, 15);
  return `${gregorian.year}-${pad2(gregorian.month)}`;
};

export function formatJalaliDate(value) {
  return formatWithOptions(arguments.length === 0 ? new Date() : value, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

export function formatJalaliDateTime(value) {
  return formatWithOptions(arguments.length === 0 ? new Date() : value, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatJalaliTime(value) {
  return formatWithOptions(arguments.length === 0 ? new Date() : value, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export const formatJalaliMonthName = (monthNumber, year = new Date().getFullYear()) => {
  const month = normalizeMonthNumber(monthNumber);
  const numericYear = Number(year);
  if (!month || !Number.isInteger(numericYear)) return "--";
  return formatWithOptions(new Date(numericYear, month - 1, 15), { month: "long" });
};

export function formatJalaliYear(value) {
  return formatWithOptions(arguments.length === 0 ? new Date() : value, {
    year: "numeric",
  });
}

export const formatJalaliYearFromGregorianYear = (year) => {
  const numericYear = Number(year);
  if (!Number.isInteger(numericYear)) return "--";
  return formatJalaliYear(new Date(numericYear, 6, 1));
};

export const formatJalaliYearMonth = (year, monthNumber) => {
  const numericYear = Number(year);
  const month = normalizeMonthNumber(monthNumber);
  if (!Number.isInteger(numericYear) || !month) return "--";

  const date = new Date(numericYear, month - 1, 15);
  return `${formatJalaliYear(date)} - ${formatWithOptions(date, { month: "long" })}`;
};
